import { useEffect, useMemo, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import $ from 'jquery';
import { get, invert, isEmpty, isNil } from 'lodash';
import {
  ODDS_CONTAINER_TYPE, ODDS_CONTAINER_CATEGORY, MAIN_PAGE,
  E_SPORT_ID, MarketFilter, EventOrder, OddsMode, BET_LOCATION_UNKNOWN, BetTypeGroup, OptionKeys,
  Market,
} from '../common/Foundation';
import OddsPageUtils from '../components/CompactView/OddsPageComponent/OddsPageUtils';
import CommonUtil from '../common/CommonUtil';
import { defaultPages } from '../components/AccountComponent/PreferencesComponent/PreferencesConfiguaration';
import ViewOptionsAction from '../components/CompactView/ViewOptionsComponent/ViewOptionsAction';
import MemberServices from '../services/MemberService';
import { updateDefaultPeriodDesc } from '../slices/SportsSlice';
import { generateMarketId } from '../slices/SidebarHandlerSlice';

const useGetBetLocationTracking = (props) => {
  const { leagueIdUrl = '', isGetDefaultPeriodDesc } = props || {};
  const dispatch = useDispatch();
  const { periodsDescription, options, defaultPeriodDescription } = useSelector(state => state.SportsSlice, shallowEqual);
  const { betTypeGroupPref, displayMode } = useSelector(state => state.AccountsSlice.user, shallowEqual);
  const { markets } = useSelector(state => state.SidebarHandlerSlice);
  const [checkGetDefaultPeriodDesc, setCheckGetDefaultPeriodDesc] = useState(isGetDefaultPeriodDesc);
  const { mode, favourite, isTopLeague, sportId, market, betTypeGroup } = options;

  // Memoize calculated values to avoid unnecessary re-renders
  const isSearchMode = useMemo(() => OddsPageUtils.isSearchEvents(mode), [mode]);
  const isLiveCenter = useMemo(() => OddsPageUtils.isLiveCenter(mode), [mode]);
  const isLiveTV = useMemo(() => OddsPageUtils.isLiveTV(mode), [mode]);
  const isStatsPageMode = useMemo(() => OddsPageUtils.isStatsPage(mode), [mode]);
  const isLeagueIdUrlExisted = useMemo(() => OddsPageUtils.isLeagueIdUrlExisted(leagueIdUrl), [leagueIdUrl]);
  const isUnkowValueLeftMenu = isSearchMode || isLiveCenter || isLiveTV;
  const translationLanguage = { lng: 'en' };
  const enableViewOptionsKeys = useMemo(() => ViewOptionsAction.getViewOptionKeys(options), [mode, market, betTypeGroup]);
  const view = "NEW_ASIAN_VIEW";
  const navigation = "SPORTS";
  const device = "DESKTOP";

  useEffect(() => {
    if (
      !window.env.enableBetLocationTracking ||
      !checkGetDefaultPeriodDesc ||
      isEmpty(periodsDescription) ||
      !isEmpty(defaultPeriodDescription)
    ) return;
    getDefaultPeriodDesc();
  }, [window.env.enableBetLocationTracking, checkGetDefaultPeriodDesc, defaultPeriodDescription, periodsDescription]);

  const getDefaultPeriodDesc = async () => {
    const params = { locale: CommonUtil.toLocaleCode(), "_": new Date().getTime() };
    try {
      setCheckGetDefaultPeriodDesc(false);
      const res = await MemberServices.getDefaultSportPeriodDesc(params);
      if (res && res.data) {
        // Convert the response data into a more usable format
        const periodsDescList = CommonUtil.convertPeriodDescriptionData(res.data);
        dispatch(updateDefaultPeriodDesc(periodsDescList));
      }
    } catch (error) {
      setCheckGetDefaultPeriodDesc(true);
      console.error("Error fetching default period description:", error);
    }
  };

  const getMainPages = () => {
    const isLive = options.market === Market.Live;
    if (isLiveCenter) {
      return MAIN_PAGE.LIVE_CENTER;
    } else if (isStatsPageMode) {
      return MAIN_PAGE.EVENT;
    } else if (isLiveTV) {
      return MAIN_PAGE.LIVE_STREAMING;
    } else if (isTopLeague) {
      return MAIN_PAGE.PROMOTED_ENTITY;
    } else if (favourite) {
      return MAIN_PAGE.FAVORITES;
    } else if (isSearchMode) {
      return MAIN_PAGE.SEARCH;
    } else if (isLeagueIdUrlExisted) {
      return MAIN_PAGE.LEAGUE;
    } else if (sportId === E_SPORT_ID) {
      return isLive ? MAIN_PAGE.LIVE_ESPORT : MAIN_PAGE.ESPORT;
    }
    return isLive ? MAIN_PAGE.LIVE_SPORT : MAIN_PAGE.SPORT;
  };

  const getOddsContainerCategory = (isInMoreBetContaier) => {
    return isInMoreBetContaier ? ODDS_CONTAINER_CATEGORY.MORE : ODDS_CONTAINER_CATEGORY.MAIN;
  };

  const getViewOptionsValue = (key) => {
    if (!enableViewOptionsKeys.includes(key)) return BET_LOCATION_UNKNOWN;
    const mapping = {
      [OptionKeys.line]: { mappingObj: MarketFilter, optionValue: options.marketFilter },
      [OptionKeys.sort]: { mappingObj: EventOrder, optionValue: options.order },
      [OptionKeys.view]: { mappingObj: OddsMode, optionValue: options.oddsMode },
    };
    const { mappingObj, optionValue } = mapping[key] || {};
    if (isNil(mappingObj) || isNil(optionValue)) return BET_LOCATION_UNKNOWN;
    return (invert(mappingObj)[optionValue] || BET_LOCATION_UNKNOWN).toUpperCase();
  };

  const getMarketTab = () => {
    if (isUnkowValueLeftMenu) return BET_LOCATION_UNKNOWN;
    return (CommonUtil.getMarketTabName(sportId, options.market) || BET_LOCATION_UNKNOWN).toUpperCase().replace("-", "_");
  };

  const getDefaultPage = () => {
    const betTypeGroupName = invert(BetTypeGroup)[betTypeGroupPref];
    const defaultPage = get(defaultPages.find(page => page.value === betTypeGroupName), 'value', '');
    return defaultPage || defaultPages[0].value;
  };

  const getMarket = (marketName) => {
    if (isUnkowValueLeftMenu) return BET_LOCATION_UNKNOWN;
    return (CommonUtil.getMarketName(marketName, defaultPeriodDescription, sportId, translationLanguage) || BET_LOCATION_UNKNOWN).toUpperCase();
  };

  const getOddsContainerTitle = (oddsContainerType) => {
    if (isStatsPageMode || isLiveCenter || isLiveTV) return BET_LOCATION_UNKNOWN;

    const containerType = {
      isLive: oddsContainerType === ODDS_CONTAINER_TYPE.LIVE,
      isHighlight: oddsContainerType === ODDS_CONTAINER_TYPE.HIGHLIGHT,
      isEarly: oddsContainerType === ODDS_CONTAINER_TYPE.EARLY,
      isToday: oddsContainerType === ODDS_CONTAINER_TYPE.TODAY,
    };

    const otbMarket = CommonUtil.getOddsTableBarMarket(containerType.isToday, containerType.isEarly, options.market);
    const title = CommonUtil.getOTBPrefixTitle(sportId, otbMarket, containerType.isLive, containerType.isHighlight, translationLanguage);
    return (title || BET_LOCATION_UNKNOWN).toUpperCase().replace("-", "_");
  };

  const getUnknowBetLocationTracking = () => ({
    view,
    navigation,
    device,
    reuseSelection: false,
    mainPages: BET_LOCATION_UNKNOWN,
    marketTab: BET_LOCATION_UNKNOWN,
    market: BET_LOCATION_UNKNOWN,
    oddsContainerCategory: BET_LOCATION_UNKNOWN,
    oddsContainerTitle: BET_LOCATION_UNKNOWN,
    language: BET_LOCATION_UNKNOWN,
    displayMode: BET_LOCATION_UNKNOWN,
    marketType: BET_LOCATION_UNKNOWN,
    eventSorting: BET_LOCATION_UNKNOWN,
    pageType: BET_LOCATION_UNKNOWN,
    timeZone: BET_LOCATION_UNKNOWN,
    defaultPage: BET_LOCATION_UNKNOWN
  });

  const getBetLocationTracking = (event, isShareBet) => {
    if (!window.env.enableBetLocationTracking) return;
    if (isShareBet) return getUnknowBetLocationTracking();
    const selectedMarketId = generateMarketId(options.betTypeGroup, options.period);
    const currentMarket = markets.find(market => market.marketId === selectedMarketId) || {};
    const isInMoreBetContaier = $(event.currentTarget).closest('div.more-bets').length > 0;
    const oddsContainer = $(event.currentTarget).closest('.odds-container');
    const oddsContainerParent = oddsContainer.parent('div');
    const oddsContainerType = OddsPageUtils.getOddsContainerType($(oddsContainerParent).attr('class'));

    return {
      view,
      navigation,
      device,
      reuseSelection: false,
      mainPages: getMainPages(),
      marketTab: getMarketTab(),
      market: getMarket(currentMarket.marketName),
      oddsContainerCategory: getOddsContainerCategory(isInMoreBetContaier),
      oddsContainerTitle: getOddsContainerTitle(oddsContainerType),
      language: CommonUtil.getCurrentLocaleCode(),
      displayMode: displayMode,
      marketType: getViewOptionsValue(OptionKeys.line),
      eventSorting: getViewOptionsValue(OptionKeys.sort),
      pageType: getViewOptionsValue(OptionKeys.view),
      timeZone: options.timeZoneId || BET_LOCATION_UNKNOWN,
      defaultPage: getDefaultPage()
    };
  };
  return getBetLocationTracking;
};

export default useGetBetLocationTracking;


// WEBPACK FOOTER //
// ./src/app/hooks/useGetBetLocationTracking.js